/**
 * @module UseForceRecompute
 * @purpose Manages the force-recompute confirmation dialog and the guarded launch
 *   POST for the reasoning-run engine.
 * @depends services/reasoning (createReasoningRun), services/api (ApiError),
 *   i18n/utils/message-keys (resolveMessageKey)
 * @dependedOnBy pages/case/analysis/reasoning/CanvasReasoningPanel
 *
 * @rules
 * - Performance: `inFlightRef` guards re-entrancy synchronously -- state renders,
 *   the ref guards. `mountedRef` mirrors use-reasoning.ts's StrictMode-safe form.
 * - State: local useState; no shared context
 * - Errors: `errorKey`/`infoKey` are bare i18n key suffixes (e.g.
 *   'reasoning.launchError.network'), never raw backend text. `errorValues` carries
 *   the numeric `details` payload alongside `errorKey` for i18next interpolation
 *   (e.g. `{ observationCount, ceiling }`); a 503 `volume_guard_timeout` maps to its
 *   own key with `errorValues === null` since that response carries no `details`.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { resolveMessageKey } from '../i18n/utils/message-keys'
import { ApiError } from '../services/api'
import { createReasoningRun } from '../services/reasoning'

/**
 * Wire vocabulary from `From<ReasoningError> for ApiError`
 * (backend/crates/evidenx-api/src/handlers/reasoning.rs:46-86), plus the
 * pre-launch volume guard's own error codes (`volume_ceiling_exceeded` 422,
 * `volume_guard_timeout` 503).
 *
 * `reasoning_in_progress` (409) is deliberately absent: the DB constraint is
 * intercepted server-side and returned as `Ok(InProgress)`
 * (backend/crates/evidenx-core/src/services/reasoning/mod.rs:150-165), so the 409
 * arm is unreachable from this endpoint.
 */
export const KNOWN_LAUNCH_ERROR_CODES = [
  'case_access_denied',
  'no_gated_runs',
  'not_found',
  'stage6_failed',
  'internal_error',
  'volume_ceiling_exceeded',
  'volume_guard_timeout',
] as const

/**
 * @behaviour Narrows an unknown `details` payload to its finite-number entries only,
 *   dropping strings/booleans/objects/`NaN`/`Infinity`; returns `null` when nothing
 *   survives so callers can treat "no numeric details" and "no details" alike.
 */
function numericDetails(details: unknown): Record<string, number> | null {
  if (typeof details !== 'object' || details === null || Array.isArray(details)) return null

  const entries = Object.entries(details as Record<string, unknown>).filter(
    ([, value]) => typeof value === 'number' && Number.isFinite(value),
  )
  if (entries.length === 0) return null

  return Object.fromEntries(entries) as Record<string, number>
}

const ACTIVE_RUN_STATUSES: ReadonlySet<string> = new Set(['pending', 'running'])

interface LaunchFeedback {
  errorKey: string | null
  errorValues: Record<string, number> | null
  infoKey: string | null
  clear: () => void
  setError: (error: unknown) => void
  setInfo: (status: string, reused: boolean | undefined) => void
}

/**
 * @behaviour Owns the error/info key pair (plus `errorValues`, the numeric `details`
 *   sidecar for i18next interpolation) so `useForceRecompute` stays under the
 *   per-function line budget; `clear`/`setError`/`setInfo` are useCallback-stable
 *   across renders and safe to list in a caller's dependency array.
 */
function useLaunchFeedback(): LaunchFeedback {
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [errorValues, setErrorValues] = useState<Record<string, number> | null>(null)
  const [infoKey, setInfoKey] = useState<string | null>(null)

  const clear = useCallback(() => {
    setErrorKey(null)
    setErrorValues(null)
    setInfoKey(null)
  }, [])

  const setError = useCallback((error: unknown) => {
    if (error instanceof ApiError) {
      setErrorKey(resolveMessageKey(error.body?.error, KNOWN_LAUNCH_ERROR_CODES, 'reasoning.launchError'))
      setErrorValues(numericDetails(error.body?.details))
    } else {
      setErrorKey('reasoning.launchError.network')
      setErrorValues(null)
    }
  }, [])

  const setInfo = useCallback((status: string, reused: boolean | undefined) => {
    if (ACTIVE_RUN_STATUSES.has(status)) {
      setInfoKey('reasoning.launchInfo.alreadyRunning')
    } else if (reused === true) {
      setInfoKey('reasoning.launchInfo.upToDate')
    }
  }, [])

  return { errorKey, errorValues, infoKey, clear, setError, setInfo }
}

interface UseForceRecomputeResult {
  isConfirming: boolean
  isComputing: boolean
  errorKey: string | null
  errorValues: Record<string, number> | null
  infoKey: string | null
  request: () => void
  confirm: (force: boolean) => Promise<void>
  launch: (force: boolean) => Promise<void>
  cancel: () => void
}

/**
 * @behaviour request() opens the confirmation dialog; confirm(force) closes it and
 *   awaits launch(force); cancel() closes it without a POST. launch(force) is
 *   re-entrancy-guarded so two same-tick calls produce exactly one POST, and skips
 *   every state write and the onSettled call once the caller has unmounted.
 * @errors ApiError with a whitelisted wire code maps to `reasoning.launchError.<code>`;
 *   any other ApiError maps to `…generic`; a non-ApiError rejection (network/proxy
 *   kill) maps to `…network`. `errorValues` (the numeric `details` sidecar) is set
 *   alongside `errorKey` and cleared with it on every `request()`/`launch()` call.
 */
export function useForceRecompute(
  caseId: string | null,
  opts?: { onSettled?: () => void },
): UseForceRecomputeResult {
  const [isConfirming, setIsConfirming] = useState(false)
  const [isComputing, setIsComputing] = useState(false)
  const { errorKey, errorValues, infoKey, clear, setError, setInfo } = useLaunchFeedback()

  const inFlightRef = useRef(false)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const launch = useCallback(
    async (force: boolean) => {
      if (inFlightRef.current) return
      inFlightRef.current = true

      if (mountedRef.current) {
        setIsComputing(true)
        clear()
      }

      try {
        if (!caseId) return
        const row = await createReasoningRun(caseId, { force })
        if (!mountedRef.current) return
        setInfo(row.status, row.reused)
      } catch (error) {
        if (!mountedRef.current) return
        setError(error)
      } finally {
        inFlightRef.current = false
        if (mountedRef.current) {
          setIsComputing(false)
          opts?.onSettled?.()
        }
      }
    },
    [caseId, opts, clear, setInfo, setError],
  )

  const request = useCallback(() => {
    setIsConfirming(true)
    clear()
  }, [clear])

  const cancel = useCallback(() => {
    setIsConfirming(false)
  }, [])

  const confirm = useCallback(
    async (force: boolean) => {
      setIsConfirming(false)
      await launch(force)
    },
    [launch],
  )

  return { isConfirming, isComputing, errorKey, errorValues, infoKey, request, confirm, launch, cancel }
}
